import { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useEvents } from '../contexts/EventContext';
import { isCustomerRole } from '../config/navConfig';
import { useMobileNavItems, type MobileNavItem } from '../hooks/useMobileNavItems';
import { MobileMoreSheet } from './MobileMoreSheet';

const PRIMARY = '#1a6b3c';
const MUTED = '#6b7280';

/**
 * Mobile bottom navigation. Branches by role:
 *
 *  - CUSTOMER-TIER (operator) roles get a PILLAR-AWARE bar derived from the
 *    tenant's enabled modules/pillars (Home + up to 3 pillars + "More" sheet).
 *    See CustomerBottomNav.
 *
 *  - Legacy KDU / attendee roles keep the ORIGINAL hardcoded reunion tab bar,
 *    including the AttendeeMenuShell panel-index coupling. See LegacyBottomNav.
 *    This path is intentionally unchanged.
 */
export function MobileBottomNav() {
  const { user } = useAuth();
  if (isCustomerRole(user?.role || '')) {
    return <CustomerBottomNav />;
  }
  return <LegacyBottomNav />;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * CUSTOMER-TIER (operator) — pillar-aware bar
 * ────────────────────────────────────────────────────────────────────────── */

function CustomerBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { selectedEventId, enabledModules } = useEvents();

  // Tenant-level pillar modules (e.g. 'giving') from /auth/me.
  const [tenantModules, setTenantModules] = useState<string[]>([]);
  useEffect(() => {
    let cancelled = false;
    if (!user) { setTenantModules([]); return; }
    import('../api/client').then(({ apiClient }) => {
      apiClient<{ tenantModules?: string[] }>('/auth/me')
        .then((res) => { if (!cancelled) setTenantModules(res?.tenantModules ?? []); })
        .catch(() => { if (!cancelled) setTenantModules([]); });
    });
    return () => { cancelled = true; };
  }, [user]);

  const modules = selectedEventId ? enabledModules : null;
  const { primary, overflow } = useMobileNavItems(user?.role, modules, tenantModules, 4);

  const [moreOpen, setMoreOpen] = useState(false);

  const isItemActive = useCallback(
    (path: string) =>
      location.pathname === path || location.pathname.startsWith(path + '/'),
    [location.pathname],
  );

  // "More" is highlighted when the current route is one of the overflow items.
  const moreActive = overflow.some((i) => isItemActive(i.path));

  const go = useCallback((path: string) => {
    setMoreOpen(false);
    navigate(path);
  }, [navigate]);

  const slots: Array<{ key: string; icon: string; label: string; active: boolean; onTap: () => void }> =
    primary.map((item: MobileNavItem) => ({
      key: item.path,
      icon: item.icon,
      label: item.label,
      active: isItemActive(item.path),
      onTap: () => go(item.path),
    }));

  // Always append the "More" slot (5th slot).
  if (overflow.length > 0) {
    slots.push({
      key: '__more__',
      icon: '⋯',
      label: 'More',
      active: moreActive || moreOpen,
      onTap: () => setMoreOpen((v) => !v),
    });
  }

  return createPortal(
    <>
      <style>{`
        .ejt-bottom-nav-btn { touch-action: manipulation; -webkit-user-select: none; user-select: none; }
        .ejt-bottom-nav-btn:active { opacity: 0.7; }
      `}</style>

      <MobileMoreSheet
        open={moreOpen}
        items={overflow}
        activePath={location.pathname}
        onClose={() => setMoreOpen(false)}
        onLogout={() => { setMoreOpen(false); logout(); }}
      />

      <nav
        id="ejt-mobile-bottom-nav"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '64px',
          backgroundColor: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.08)',
          zIndex: 2147483647,
          display: 'flex',
          alignItems: 'stretch',
          justifyContent: 'space-around',
          touchAction: 'manipulation',
          isolation: 'isolate',
          paddingBottom: 'env(safe-area-inset-bottom)',
        }}
        aria-label="Workspace navigation"
      >
        {slots.map((slot) => (
          <button
            key={slot.key}
            className="ejt-bottom-nav-btn"
            onClick={slot.onTap}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '3px',
              flex: 1,
              minWidth: 0,
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              padding: '6px 2px',
              backgroundColor: slot.active ? '#f0fdf4' : '#ffffff',
              WebkitTapHighlightColor: 'rgba(26,107,60,0.1)',
              WebkitAppearance: 'none',
              appearance: 'none',
              outline: 'none',
            }}
            aria-label={slot.label}
            aria-current={slot.active ? 'page' : undefined}
          >
            <span style={{ fontSize: '1.3rem', lineHeight: 1, pointerEvents: 'none' }}>
              {slot.icon}
            </span>
            <span
              style={{
                fontSize: '0.62rem',
                lineHeight: 1.1,
                textAlign: 'center',
                color: slot.active ? PRIMARY : MUTED,
                fontWeight: slot.active ? 700 : 500,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: '100%',
                pointerEvents: 'none',
              }}
            >
              {slot.label}
            </span>
          </button>
        ))}
      </nav>
    </>,
    document.body,
  );
}

/* ─────────────────────────────────────────────────────────────────────────────
 * LEGACY KDU / ATTENDEE — original hardcoded reunion bar (UNCHANGED behavior)
 * ────────────────────────────────────────────────────────────────────────── */

/**
 * panelIndex: the AttendeeMenuShell section index (0-9), or -1 for React Router navigation
 */
const NAV_ITEMS = [
  { icon: '🏠', label: 'My Weekend', path: '/my-weekend',             panelIndex: 0 },
  { icon: '🧰', label: 'Toolkit',    path: '/guest-toolkit',          panelIndex: 1 },
  { icon: '📅', label: 'Events',     path: '/events',                 panelIndex: 2 },
  { icon: '🛒', label: 'Market',     path: '/marketplace',            panelIndex: 3 },
  { icon: '❓', label: 'Questions',  path: '/guest-questions',         panelIndex: -1 },
  { icon: '📢', label: 'News',       path: '/announcements',          panelIndex: 4 },
  { icon: '📍', label: 'Nearby',     path: '/nearby',                 panelIndex: -1 },
  { icon: '🌍', label: 'Chapters',   path: '/chapters',               panelIndex: 5 },
  { icon: '✨', label: 'Krio Facts', path: '/krio-fun-facts',         panelIndex: 6 },
  { icon: '💡', label: 'Knowledge',  path: '/knowledge',              panelIndex: 7 },
  { icon: '📖', label: 'My Story',   path: '/story-builder/my-story', panelIndex: 8 },
  { icon: '⚙️', label: 'Settings',   path: '/settings',               panelIndex: 9 },
];

function LegacyBottomNav() {
  const location = useLocation();
  const navigate = useNavigate();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Track active panel index (for AttendeeMenuShell users)
  const [activePanelIndex, setActivePanelIndex] = useState(0);

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail && typeof detail.index === 'number') {
        setActivePanelIndex(detail.index);
      }
    };
    window.addEventListener('navigate-to-panel', handler);
    return () => window.removeEventListener('navigate-to-panel', handler);
  }, []);

  // Use onPointerDown + onPointerUp for reliable mobile taps (bypasses iOS touch delay)
  const handleItemTap = useCallback((item: typeof NAV_ITEMS[number]) => {
    if (item.panelIndex >= 0) {
      // Dispatch panel navigation event (AttendeeMenuShell listens to this)
      window.dispatchEvent(
        new CustomEvent('navigate-to-panel', { detail: { index: item.panelIndex } })
      );
      // For non-Attendee users (Admin/Organizer/Delegate), also navigate via router
      navigate(item.path);
    } else {
      // Items outside the shell (My Story) — always use React Router
      navigate(item.path);
    }
  }, [navigate]);

  return createPortal(
    <>
      <style>{`
        .kdu-bottom-nav-scroll::-webkit-scrollbar { display: none; }
        .kdu-bottom-nav-scroll { -ms-overflow-style: none; scrollbar-width: none; }
        .kdu-bottom-nav-btn { touch-action: manipulation; -webkit-user-select: none; user-select: none; }
        .kdu-bottom-nav-btn:active { opacity: 0.7; }
      `}</style>

      <nav
        id="kdu-mobile-bottom-nav"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          height: '64px',
          backgroundColor: '#ffffff',
          borderTop: '2px solid #e5e7eb',
          boxShadow: '0 -4px 12px rgba(0,0,0,0.1)',
          zIndex: 2147483647, // max possible z-index
          display: 'flex',
          alignItems: 'stretch',
          touchAction: 'manipulation',
          isolation: 'isolate', // creates new stacking context
        }}
        aria-label="Mobile bottom navigation"
        onTouchStart={(e) => e.stopPropagation()}
      >
        <div
          ref={scrollRef}
          className="kdu-bottom-nav-scroll"
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            overflowX: 'scroll',
            overflowY: 'hidden',
            flex: 1,
            padding: '0 4px',
            touchAction: 'pan-x',
          }}
        >
          {NAV_ITEMS.map((item) => {
            // Active detection: panel items use activePanelIndex, router items use pathname
            const isActive = item.panelIndex >= 0
              ? activePanelIndex === item.panelIndex
              : location.pathname === item.path || location.pathname.startsWith(item.path + '/');

            return (
              <button
                key={item.path}
                className="kdu-bottom-nav-btn"
                onClick={() => handleItemTap(item)}
                onTouchEnd={(e) => {
                  // Ensure tap fires even if onClick is swallowed
                  e.preventDefault();
                  handleItemTap(item);
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                  flexShrink: 0,
                  minWidth: '64px',
                  width: '64px',
                  height: '56px',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  padding: '4px 2px',
                  backgroundColor: isActive ? '#f0fdf4' : '#ffffff',
                  WebkitTapHighlightColor: 'rgba(26, 107, 60, 0.1)',
                  touchAction: 'manipulation',
                  WebkitAppearance: 'none',
                  appearance: 'none',
                  outline: 'none',
                }}
                aria-label={item.label}
                aria-current={isActive ? 'page' : undefined}
              >
                <span style={{ fontSize: '1.25rem', lineHeight: 1, pointerEvents: 'none' }}>
                  {item.icon}
                </span>
                <span
                  style={{
                    fontSize: '0.6rem',
                    lineHeight: 1.1,
                    textAlign: 'center',
                    color: isActive ? PRIMARY : MUTED,
                    fontWeight: isActive ? 700 : 500,
                    whiteSpace: 'nowrap',
                    maxWidth: '62px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    pointerEvents: 'none',
                  }}
                >
                  {item.label}
                </span>
              </button>
            );
          })}
        </div>
      </nav>
    </>,
    document.body
  );
}
